// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { cn, stringToHue } from '@/lib/utils';

export interface AppIconTileProps {
  iconUrl?: string | null;
  name: string;
  slug?: string;
  /** Tile side in px. Default 40. */
  size?: number;
  /** Corner radius in px. Default 12. */
  radius?: number;
  className?: string;
}

const FIRST_ALPHANUMERIC = /[a-z0-9]/i;

/**
 * Square tile that renders an app's icon image when available, or a
 * deterministic monogram on a brand-tinted background. Falls back to the
 * monogram if the image fails to load (404, broken URL).
 * Purely decorative — the adjacent app name carries the accessible label.
 */
export function AppIconTile({
  iconUrl,
  name,
  slug,
  size = 40,
  radius = 12,
  className,
}: AppIconTileProps): React.JSX.Element {
  // Fall back to the monogram if the image errors. Reset when the URL changes
  // so a later-corrected URL gets another chance to load.
  const [imgFailed, setImgFailed] = React.useState(false);
  React.useEffect(() => setImgFailed(false), [iconUrl]);

  const baseClasses = cn(
    'inline-flex shrink-0 items-center justify-center overflow-hidden',
    className,
  );

  const showImage = iconUrl != null && iconUrl !== '' && !imgFailed;
  if (showImage) {
    return (
      <span
        aria-hidden="true"
        className={cn(baseClasses, 'border border-border bg-card')}
        style={{ width: size, height: size, borderRadius: radius }}
      >
        <img
          src={iconUrl}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImgFailed(true)}
        />
      </span>
    );
  }

  const seed = slug ?? name;
  const hue = stringToHue(seed);

  const source = name.length > 0 ? name : (slug ?? '');
  const match = source.match(FIRST_ALPHANUMERIC);
  const letter = match?.[0]?.toUpperCase() ?? '?';

  return (
    <span
      aria-hidden="true"
      className={cn(
        baseClasses,
        'border border-black/10 font-mono font-semibold text-white dark:border-white/15',
      )}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        fontSize: Math.round(size * 0.38),
        backgroundColor: `hsl(${hue} 55% 45%)`,
      }}
    >
      {letter}
    </span>
  );
}
